import { useEffect } from 'react';
import Select from 'react-select';
import { TaskActionStatus, TaskAssigneeType } from '../taskType';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { usePatchAction } from '../hooks';
import { useToast } from '../../../../../context/ToastContext';
import CareMapprButton from '../../../Button/CareMapprButton';
import { useTriggerUpdate } from '../../../../../context';

const sanitizedCreateTaskInput = (values) => {
  const input = {
    description: values?.taskName?.value ?? '',
    details_description: values?.description ?? '',
    action_status: TaskActionStatus.DEFAULT,
  };

  if (values.dueDate) {
    input.due_date = moment(values.dueDate).toISOString();
  }

  if (values.actionId) {
    input.actionId = values.actionId;
  }

  if (values.taskType === TaskAssigneeType.INDIVIDUAL) {
    input.clinician_id = values.assignedPerson.value;
  } else if (values.taskType === TaskAssigneeType.TEAM) {
    input.team_id = values.assignedTeam.value;
  } else {
    return 'INPUT is invalid';
  }

  return input;
};
export const TaskForm = ({
  onClose,
  form,
  clinicianOptions,
  teamsOptions,
  taskNameOptions,
  isOptionsLoading,
}) => {
  const {
    register,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { isValid },
  } = form;
  const patient_id = useSelector(
    (state) => state.patientProfile.patientProfile.id,
  );

  const { isLoading, postPatchAction } = usePatchAction();
  const { setToast } = useToast();

  const taskType = watch('taskType');
  const assignedTeam = watch('assignedTeam');
  const assignedPerson = watch('assignedPerson');
  const taskName = watch('taskName');

  const actionId = watch('actionId');
  const assignedPersonId = watch('assignedPersonId');
  const assignedTeamId = watch('assignedTeamId');
  const taskNameId = watch('taskNameId');

  const assigned = assignedTeam || assignedPerson;

  const handleSubmitTaskForm = async (values) => {
    const sanitizedUserInputs = sanitizedCreateTaskInput(values);
    const payload = {
      ...sanitizedUserInputs,
    };
    if (payload.actionId) {
      setToast('Updating task...');
    } else {
      setToast('Creating task...');
    }
    await postPatchAction(payload);

    if (payload.actionId) {
      setToast('Task updated!');
    } else {
      setToast('Task created!');
    }

    reset();
    onClose();
  };

  useEffect(() => {
    if (assignedPersonId) {
      const index = clinicianOptions.findIndex(
        (clinicianOption) => clinicianOption.value === assignedPersonId,
      );
      if (index > -1) setValue('assignedPerson', clinicianOptions[index]);
    }
  }, [assignedPersonId]);

  useEffect(() => {
    if (assignedTeamId) {
      const index = teamsOptions.findIndex(
        (teamsOption) => teamsOption.value === assignedTeamId,
      );
      if (index > -1) setValue('assignedTeam', teamsOptions[index]);
    }
  }, [assignedTeamId]);

  useEffect(() => {
    if (taskNameId) {
      const index = taskNameOptions.findIndex(
        (taskNameOption) => taskNameOption.value === taskNameId,
      );
      if (index > -1) setValue('taskName', taskNameOptions[index]);
    }
  }, [taskNameId]);

  // clear oppsite assignee value to prevent value in-consistent
  useEffect(() => {
    if (taskType === TaskAssigneeType.INDIVIDUAL) {
      setValue('assignedTeam', null);
    } else if (taskType === TaskAssigneeType.TEAM) {
      setValue('assignedPerson', null);
    }
  }, [taskType]);

  return (
    <>
      <form onSubmit={handleSubmit(handleSubmitTaskForm)}>
        <div>{actionId ? 'Edit' : 'Add'} task</div>
        <div className="assign-type">
          <label className="txtDropdownLabel">Assign to:</label>
          <div className="form-check">
            <input
              {...register('taskType')}
              className="form-check-input"
              type="radio"
              value={TaskAssigneeType.INDIVIDUAL}
            />
            <label className="form-check-label">Individual</label>
          </div>
          <div className="form-check">
            <input
              {...register('taskType')}
              className="form-check-input"
              type="radio"
              value={TaskAssigneeType.TEAM}
            />
            <label className="form-check-label">Team</label>
          </div>
        </div>
        <div className="filterItem">
          <div className="divSelect width-full">
            {taskType === TaskAssigneeType.INDIVIDUAL ? (
              <Select
                {...register('assignedPerson')}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    fontWeight: 300,
                  }),
                }}
                isMulti={false}
                isLoading={isOptionsLoading}
                menuSize={800}
                options={clinicianOptions}
                value={assignedPerson}
                placeholder={'Please select'}
                showTick={true}
                onChange={(selectedOptions) => {
                  setValue('assignedPerson', selectedOptions);
                }}
              />
            ) : null}

            {taskType === TaskAssigneeType.TEAM ? (
              <Select
                {...register('assignedTeam')}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    fontWeight: 300,
                  }),
                }}
                isMulti={false}
                isLoading={isOptionsLoading}
                menuSize={800}
                options={teamsOptions}
                value={assignedTeam}
                placeholder={'Please select'}
                showTick={true}
                onChange={(selectedOptions) => {
                  setValue('assignedTeam', selectedOptions);
                }}
              />
            ) : null}
          </div>
        </div>
        <div className="filterItem" style={{ marginTop: '10px' }}>
          <div>
            <label className="txtDropdownLabel">Task</label>
          </div>
          <div className="divSelect width-full">
            <Select
              {...register('taskName', { required: true })}
              isMulti={false}
              menuSize={800}
              options={taskNameOptions}
              value={taskName}
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  fontWeight: 300,
                }),
              }}
              placeholder={'Please select'}
              showTick={true}
              onChange={(selectedOptions) => {
                setValue('taskName', selectedOptions);
              }}
            />
          </div>
        </div>
        <div style={{ marginTop: '10px' }}>
          <div className="filterItem">
            <div>
              <label className="txtDropdownLabel" name="FurtherDescription">
                Further description (optional)
              </label>
            </div>

            <input
              {...register('description')}
              type="text"
              label="Further description"
              className="med-input"
              placeholder="Start typing..."
            />
          </div>
        </div>
        <div>
          <div className="filterItem date-container">
            <div style={{ width: '50%', display: 'inline-block' }}>
              <label className="txtDropdownLabel" name="FurtherDescription">
                Due date
              </label>
            </div>

            <input
              {...register('dueDate', { required: true })}
              type="date"
              label="Further description"
              className="date-input"
              placeholder="Select date"
            />
          </div>
        </div>
        <div className="task-actions">
          <CareMapprButton
            type="button"
            onClick={onClose}
            isLoading={isLoading}
            disabled={isLoading}
            variant="transparent"
            style={{ marginRight: '10px' }}
          >
            <span style={{ fontSize: '15px', color: '#2b2b2b' }}>
              Cancel + close
            </span>
          </CareMapprButton>
          <CareMapprButton
            type="submit"
            isLoading={isLoading}
            disabled={isLoading || !isValid || !assigned}
          >
            <span className="px-2" style={{ fontSize: '15px' }}>
              Save task
            </span>
          </CareMapprButton>
        </div>
      </form>
    </>
  );
};
